import type {ImageCatalog} from 'microsites/core/chatMicrosite/images/imageTypes';

// NOTE: Never import raw SVGs (e.g., ornamentDivider) in this file—doing so breaks our Gatsby SSR pipeline.

const imageCatalog = {
  aceAndProudAlt: {
    id: `aceAndProudAlt`,
    kind: `raster`,
    width: 1024,
    height: 1024,
    description: `Realistic selfie of a Middle Eastern woman wearing a mustard sweater with an ace pride flag pin, softly lit against a teal wall for a natural, warm look.`,
    prompt: `Realistic 1:1 selfie of a Middle Eastern woman in a completely different setting and outfit. Casual natural lighting, different clothes and background—perhaps outdoors at sunset or in a cozy modern room. Authentic expression, realistic and candid, warm tones but distinct from previous café images.`,
  },
  aceAndProudSelfie: {
    id: `aceAndProudSelfie`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Casual selfie of the same asexual woman from the café scene, taken at a slight upward angle with imperfect indoor lighting and natural skin texture for an authentic, unposed feel.`,
    prompt: `Casual, unposed selfie of the same woman from the café, captured at a slight angle with imperfect indoor lighting and natural skin texture. Looks like a phone selfie—slightly blurry, realistic, authentic, and unedited.`,
  },
  alleyCornerCoffeePortrait: {
    id: `alleyCornerCoffeePortrait`,
    kind: `raster`,
    width: 1024, // portrait ratio recommended
    height: 1536,
    description: `Cinematographic, moody portrait of two Black men in a dark alley corner, holding takeaway coffees and sharing an intimate chat.`,
    prompt: `Photorealistic photo, cinematographic, portrait ratio — no table, just in a dark corner.
Two African American men stand close in a dimly lit alley, engaging in a conversation while holding disposable coffee cups. One man, in a brown corduroy jacket, has his arm around the other, who wears a faded black denim jacket, and both are illuminated by a warm reddish-orange light that casts dramatic shadows on their faces amidst the gritty, textured brick backdrop.`,
  },
  beachLifestyleSunrise: {
    id: `beachLifestyleSunrise`,
    kind: `raster`,
    width: 1536,
    height: 1024,
    description: `Queer couple sharing sunrise flat whites on the Algarve coastline with gentle pastel waves and airy warmth.`,
    prompt: `3:2 ratio — Sunrise beach walk scene on the Algarve coast featuring a queer couple holding takeaway flat whites. Soft pastel sky gradients, gentle Atlantic waves, minimal footprints in the sand, cozy knit layers with modern athleisure, candid smiles, Fujifilm GFX medium-format look, airy diffusion filter, evokes calm intimacy and optimistic new beginnings. Compose at 4500x3000 for responsive desktop banners, with leading lines from shoreline toward the couple, gentle lens flare, high dynamic range for soft highlights.`,
  },
  valueProps1: {
    id: `valueProps1`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Diverse adults with visible differences and disabilities standing together outdoors in warm daylight, smiling and confident — promoting inclusion and body positivity.`,
    prompt: `Outdoor portrait for a body-positivity and inclusion campaign. 
A diverse group of adults — including people with visible scars, limb differences, or other nontraditional features — 
standing together in natural light. They’re smiling, confident, and connected. 
The vibe is real and uplifting, not airbrushed — showing that everyone belongs. 
Warm daylight, relaxed poses, casual clothing, earthy or city-park background. 
Editorial-quality, documentary-style photography.`,
  },
  conversionMobile: {
    id: `conversionMobile`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Two friends toast iced coffees over a tablet showing playful, secure ChatCoffee messages in sunset hues.`,
    prompt: `9:16 ratio — Craft a cheerful scene of two friends clinking iced coffees while leaning over a shared tablet that shows a flirty ChatCoffee conversation with secure-lock badges. Include warm sunset tones, sparkly chat bubbles, and soft gradients in brand colors; emphasize connection, safety, and fun energy. Render in crisp vector style suitable for high-resolution export.`,
  },
  valueProps2: {
    id: `valueProps2`,
    kind: `raster`,
    width: 1024,
    height: 1024,
    description: `Two women cuddle on a sofa under soft pink neon, cozy blanket, with coffee on the side table—cinematic, intimate, PG-13.`,
    prompt: `The photograph shows two women with light skin tenderly curled up on a dark sofa, bathed in the soft pink glow of a neon triangle on the brick wall above. They lie close together beneath a textured beige blanket, sharing a quiet, intimate moment, with subtle expressions of contentment on their faces while a side table with two mugs catches the neon light beside them.`,
  },
  featureIcons: {
    id: `featureIcons`,
    kind: `raster`,
    width: 1536,
    height: 1024,
    description: `Collection of illustrated feature icons with gradients tailored to the ChatCoffee brand palette.`,
    prompt: null,
  },
  firstSwipeEspresso: {
    id: `firstSwipeEspresso`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Same-sex couple clinking demitasse in a chic, warmly lit hotel café; landing-page hero for a dating app.`,
    prompt: `In a high-quality photo, a same-sex couple shares a joyful moment at a chic café, clinking their white porcelain espresso cups while gazing at each other with smiles. The African American individual sports short curly hair and a beige sweater with a brown tweed blazer, while their Caucasian partner, with wavy reddish-brown hair and a navy-blue blazer, shares the intimate gesture, both surrounded by a softly blurred, warmly lit café interior showing marble tables and cozy furnishings.`,
  },
  heroCoffee: {
    id: `heroCoffee`,
    kind: `raster`,
    width: 1536,
    height: 1024,
    description: `Hero background art of the ChatCoffee skyline brew scene used behind the primary hero section.`,
    prompt: null,
  },
  heroMobile: {
    id: `heroMobile`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Joyful couple sharing laughs over cappuccinos in a modern coffee bar with playful encrypted chat bubbles.`,
    prompt: `9:16 ratio — Illustrate a joyful couple sharing laughs over steaming cappuccinos inside a modern coffee bar, both holding phones that display playful encrypted chat bubbles with hearts and sparkles. Warm ambient lighting, soft gradients in ChatCoffee colors, subtle privacy icons, clean vector style with high-resolution 4K output.`,
  },
  infinityPoolSunsetPortrait: {
    id: `infinityPoolSunsetPortrait`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Gay couple relaxing with specialty coffee beside an infinity pool at sunset with a luxe city backdrop.`,
    prompt: `portrait of a gay couple chatting over fancy coffee beside an infinity pool at sunset, high-rise skyline in the background, cinematic lighting, warm golden tones, high-end fashion photography, shallow depth of field, natural candid smiles, luxury lifestyle atmosphere — portrait ratio, ultra-detailed, professional lens aesthetic`,
  },
  loungeLifestyleEvening: {
    id: `loungeLifestyleEvening`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Twilight Lisbon coffee lounge with queer friends laughing over cappuccinos amid neon reflections and warm bokeh.`,
    prompt: `4:5 ratio — Ultra-detailed lifestyle photo inspired by an upscale Lisbon coffee lounge at twilight. Two queer-presenting friends laugh over cappuccinos and pastel de nata on a marble table, warm neon signage reflecting across industrial windows, soft bokeh string lights, candid storytelling moment, premium mirrorless full-frame depth of field, cinematic teal and rose color grade, natural body language, casual streetwear with subtle pride accessories, magazine editorial lighting. Compose for 1080x1350 mobile-first hero slots, sharp focus on faces while retaining ambient glow, export as 4K portrait.`,
  },
  neonLoungePortrait: {
    id: `neonLoungePortrait`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Affirming neon lounge gathering of trans and nonbinary friends sharing a cozy, tech-forward chat moment.`,
    prompt: `Cinematic high-end portrait photograph of a cozy modern indoor lounge with soft neon lighting in pastel pink, blue, and white tones inspired by the trans pride flag. A diverse group of trans and nonbinary friends sits together on a couch, chatting warmly and sharing something on a phone or tablet. The atmosphere feels affirming and safe, with comfortable seating, soft focus background, and gentle depth of field. Stylish, contemporary outfits showing varied gender expression. Warm ambient glow, subtle reflections, and bokeh lighting create a premium, cinematic mood. Portrait orientation, realistic photography.`,
  },
  nonbinaryCafePortrait: {
    id: `nonbinaryCafePortrait`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Intimate portrait of a nonbinary person in a cozy café, reading a zine titled “Consent Culture” with a latte in the foreground. Confident expression, soft light, and Pride hues in the blurred background.`,
    prompt: `Intimate portrait of a nonbinary person in a luxe café nook, 
wearing a minimalist leather day collar as jewelry (subtle), reading a zine titled “Consent Culture,” 
latte art in foreground. Expression confident, inviting; signage with Pride hues blurred behind.
Tech: 50mm f/1.8, soft key from window, gentle film grain. Portrait.`,
  },
  ornamentDivider: {
    id: `ornamentDivider`,
    kind: `vector`,
    description: `Linear ornament divider used to separate major landing page sections.`,
    prompt: null,
  },
  author01: {
    id: `author01`,
    kind: `raster`,
    width: 720,
    height: 1080,
    description: `Portrait of the ChatCoffee editorial lead captured in soft daylight with a latte-toned backdrop for author bios and metadata.`,
    prompt: null,
  },
  plusSizeTrioPortrait: {
    id: `plusSizeTrioPortrait`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Three plus-size adults — two women and one man — standing close together in a cozy, sunlit lounge. Warm, affectionate, and body-positive atmosphere celebrating confidence and inclusion.`,
    prompt: `Portrait photo of three plus-size adults — two women and one man, racially diverse — standing close together 
in a softly lit, cozy lounge. They are dressed in towels or robes, relaxed and confident, conveying warmth, 
intimacy, and body positivity without explicit nudity. Natural light, earthy tones, plants, and woven textures 
create a respectful and inclusive atmosphere. Shot on a Leica SL2 with shallow depth of field, realistic skin tones, 
and editorial advertising quality.`,
  },
  queerCafeRopeCorner: {
    id: `queerCafeRopeCorner`,
    kind: `raster`,
    width: 1536,
    height: 1024,
    description: `Warm, intimate café corner with coiled natural rope on a brass hook, espresso on terrazzo table, and two people laughing in background near a “Rope Workshop” flyer.`,
    prompt: `Gallery-like corner of a queer café: beautifully coiled natural rope displayed on a brass hook with a plant, espresso on a terrazzo table. Two people laugh in background bokeh, pointing at a flyer “Intro Rope Workshop—Beginners Welcome.” No rope on bodies.
Tech: 35mm f/2.2, spotlight pin on rope, warm ambient fill. Landscape.`,
  },
  rooftopLifestyleNight: {
    id: `rooftopLifestyleNight`,
    kind: `raster`,
    width: 1536,
    height: 1024,
    description: `Golden-to-blue hour rooftop coffee cocktails with friends against a glowing São Paulo skyline.`,
    prompt: `16:9 ratio — Golden-hour to blue-hour rooftop scene in São Paulo capturing a mixed group of late-20s friends sharing iced coffee cocktails. Layered city skyline, fairy lights overhead, reclaimed wood furniture, candid laughter, expressive hand gestures, premium street-fashion with earth-tone palettes, Leica SL2 50mm, shallow depth of field, cinematic grain, mood of spontaneous connection and inclusive energy. Compose for 3840x2160 desktop hero with negative space for headlines, keep horizon level, maintain crisp detail from foreground to skyline using focus stacking.`,
  },
  shelterGroupPortrait: {
    id: `shelterGroupPortrait`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Humanitarian-style portrait of four people inside a community shelter, wearing worn clothing with visible signs of labor and fatigue, portrayed with empathy and dignity under warm indoor lighting.`,
    prompt: `Humanitarian portrait of a small group of people inside a community shelter. They appear tired from hardship but dignified, with natural signs of wear like dusty clothes and rough hands. Warm indoor lighting, compassionate tone, realistic photography style, portrait orientation.`,
  },
  shepherdMountainSelfie: {
    id: `shepherdMountainSelfie`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Amateur-style selfie of a smiling shepherd on a Swiss mountain, surrounded by happy cows and sheep under cloudy light — a candid, natural family-photo vibe.`,
    prompt: `Amateur-style selfie of a cheerful shepherd on a mountain in Switzerland, 
surrounded by happy cows and sheep. The image has realistic bad lighting, 
slightly uneven exposure, and a casual, authentic family-photo feel. 
The shepherd smiles warmly at the camera, with alpine scenery and cloudy sky in the background. 
Realistic textures, documentary feel.`,
  },
  shepherdSunset: {
    id: `shepherdSunset`,
    kind: `raster`,
    width: 1536,
    height: 1024,
    description: `Golden-hour group portrait of a smiling shepherd with three women in faux-shearling outfits, standing in a pasture of sheep.`,
    prompt: `Source image provided by user (non-AI photo). No generative prompt available.
If this asset is ever re-generated with an image model, paste the exact prompt here.`,
  },
  sunsetSkybarPortrait: {
    id: `sunsetSkybarPortrait`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Romantic skybar sunset portrait with a stylish couple enjoying signature coffee drinks above the city.`,
    prompt: `(Not provided)`,
  },
  testimonialsBg: {
    id: `testimonialsBg`,
    kind: `raster`,
    width: 1536,
    height: 1024,
    description: `Abstract gradient background designed for testimonial sections with soft ambient lighting effects.`,
    prompt: null,
  },
  yachtCouplePortrait: {
    id: `yachtCouplePortrait`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Luxury travel selfie of a smiling couple aboard a yacht, the man wearing a linen shirt and watch, and the woman in a gold dress leaning in affectionately. Warm sunlight, blue sea, and editorial elegance.`,
    prompt: `Portrait of a couple enjoying a sunny day on a premium yacht. 
The man, stylish in a linen shirt and luxury watch, smiles as his partner in a gold evening dress leans close, both laughing warmly. 
The sea glows blue around them, with soft cinematic depth of field and an editorial luxury-travel tone.`,
  },
  naturistLoungePortrait: {
    id: `naturistLoungePortrait`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `A warm, sunlit lounge scene depicting diverse adults in towels and light robes, relaxed in conversation — conveying body confidence, inclusion, and trust.`,
    prompt: `Portrait-orientation marketing photo for a naturist-friendly, body-positive social app. 
A warm, sunlit lounge with soft natural light and neutral earth tones. 
Diverse adults in their late 20s–40s sit comfortably on linen sofas and rattan chairs, 
wrapped in towels or light robes, relaxed and smiling in natural conversation — 
no explicit nudity, only tasteful, inclusive atmosphere. 
The mood conveys body confidence, trust, and consent-first connection. 
Modern minimal interior with plants, woven textures, and soft highlights. 
Shot on a Leica SL2 / Canon R5 with shallow depth of field, realistic skin tones, fine detail, 
and soft cinematic grain. Balanced composition with space for text on the upper third — 
editorial advertising quality, absolutely no AI artifacts or uncanny features.`,
  },
  plus18GroupPhoto: {
    id: `plus18GroupPhoto`,
    kind: `raster`,
    width: 3840,
    height: 5120,
    description: `Adult-only meetup group posing with coffees at a ChatCoffee lounge pop-up.`,
    prompt: null,
  },
  gayGroupPhoto: {
    id: `gayGroupPhoto`,
    kind: `raster`,
    width: 3840,
    height: 5120,
    description: `Group of gay ChatCoffee friends grinning together during an in-person hang.`,
    prompt: null,
  },
  chatHomePagePhoto: {
    id: `chatHomePagePhoto`,
    kind: `raster`,
    width: 3840,
    height: 5120,
    description: `iPhone running the ChatCoffee home screen with live rooms and prompts visible.`,
    prompt: null,
  },
} as const;

export const images = imageCatalog satisfies ImageCatalog<typeof imageCatalog>;

export type ImageKey = keyof typeof images;
